import Box from '@mui/material/Box'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Big from 'big.js'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import BasicModal from './BasicModal'
import { TextButtonVariant } from '../Buttons/TextButton'
import { Button, CircularProgress, TextField, useTheme } from '@mui/material'
import {
  Address,
  PaymentStartRequestBodyForTypeEnum,
  PaymentMethodKey,
  PaymentResponse,
} from '../../swagger'
import AddressForm from '../Address/AddressForm'
import CheckCircle from '@mui/icons-material/CheckCircle'
import getLocaleCurrencyForAmount from '../../utils/getLocaleCurrencyForAmount'
import { FieldValidity } from '../Interfaces/FieldValidity'
import { extractedErrorObject, paymentsApi } from '../../api/swagger'
import { startPayment, updatePaymentAddress } from '../../api/payments'
import ErrorAlert from '../Alerts/ErrorAlert'
import InvalidAddress, {
  InvalidAddressVariant,
} from '../Address/InvalidAddress'
import AddressSelect from '../Address/AddressSelect'
import { useCountryContext } from '../Context/CountryContext'
import { LoadingContext } from '../Context/LoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import {
  convertTwoDecimalStringToBig,
  isValidTwoDecimalString,
} from '../../utils/bigJSHelper'
import {
  validateAddress as validateAddressUtil,
  geocodeAddress as geocodeAddressUtil,
} from '../../utils/addressValidation'
import {
  ValidationMessagesEnum,
  getPaymentValidationMessagesConfig,
} from './utils/paymentValidationMessages'
import {
  PaymentModalSteps,
  PaymentBillingAddressValidationSteps,
  initialAmountOption,
  LcdAmountSelection,
} from './utils/paymentModalSteps'
import {
  handleChoosePaymentMethod,
  handleEnterAmount,
  handleEnterBillingAddress,
  handleEnterPaymentInformation,
  handleLCDStep,
  handleLCDAmountStep,
} from './utils/paymentModalHandlers'
import EnterAmountContent from './PaymentModal/components/EnterAmountContent'
import { PaymentCompleteContent } from './PaymentModal/components/PaymentCompleteContent'
import { PaymentCompleteZeroDueContent } from './PaymentModal/components/PaymentCompleteZeroDueContent'
import PaymentMethodSelection from './PaymentModal/components/PaymentMethodSelection'
import { EnterPaymentInformationContent } from './PaymentModal/components/EnterPaymentInformationContent'
import LCDOptionsContent from './PaymentModal/components/LCDOptionsContent'
import useValidationMessages from '../../hooks/useValidationMessages'
import {
  useBankPaymentForm,
  useBillingAddressForm,
  usePaymentAmount,
  emptyAddressInformation,
} from './PaymentModal/hooks'
import {
  PaymentModalProps,
  PaymentModalVariant,
  LcdProgramSelection,
} from './PaymentModal/types'
import LCDProgramSelectionComponent from './PaymentModal/components/LCDProgramSelection'

const defaultCurrencyCode = 'USD'

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose: yesCancel,
  variant = PaymentModalVariant.Enrollment,
  unpaidEnrollments,
  programKey = -1,
  actorKey,
  amountDue,
  amountOwed,
  achDiscount,
  isLcd = false,
  programs,
  loadingIdsToAddOnComplete,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isLicensing = variant === PaymentModalVariant.Licensing
  /** Value is set to `undefined` since the address `validation` step has not been started  */
  const initialValueForPaymentBillingAddress = undefined
  const { countryOptions } = useCountryContext()
  const {
    PaymentModal: { fetchStates: fetchStatesLoadingId },
  } = useLoadingIds()
  const { addLoadingIds } = React.useContext(LoadingContext)
  /** State Objects */
  const [isCardSelected, setIsCardSelected] = useState(false)
  const [isBankSelected, setIsBankSelected] = useState(isLicensing)

  const [cancelProcess, setCancelProcess] = useState(false)
  const [achAllowed, setAchAllowed] = useState(false)

  const [selectedPrograms, setSelectedPrograms] = useState<number[]>([])

  const [lcdProgramSelections, setLcdProgramSelections] = useState<
    LcdProgramSelection[]
  >([])

  const lcdTotalAmount = useMemo(() => {
    return lcdProgramSelections.reduce(
      (sum, selection) =>
        sum.plus(convertTwoDecimalStringToBig(selection.amount)),
      Big(0)
    )
  }, [lcdProgramSelections])

  const [
    stepToValidatePaymentBillingAddress,
    setStepToValidatePaymentBillingAddress,
  ] = useState<PaymentBillingAddressValidationSteps | undefined>()

  const [lcdAmountOption, setLCDAmountOption] =
    useState<LcdAmountSelection>('payTotalAmountDue')

  useEffect(() => {
    if (!isLicensing) {
      const fetchAchAllowed = async () => {
        try {
          const options =
            await paymentsApi.fetchEnrollmentBillingPaymentOptions()
          setAchAllowed(!!options.achAllowed)
        } catch (err) {
          const errorObject = (await extractedErrorObject(err)) ?? {
            code: 'UnknownError',
            message:
              (err as unknown as Error).message ??
              'Failed to fetch program details.',
          }
          setErrorMessage(errorObject.message)
          setSubmissionInProgress(false)
        }
      }
      fetchAchAllowed()
    }
  }, [isLicensing, programKey, t, achAllowed])

  // If there is no amount due for an Enrollment Payment, then we want skip to the end of the payment flow
  const shouldShortCircuitPaymentFlow =
    !isLicensing &&
    (!amountDue.amount || convertTwoDecimalStringToBig(amountDue.amount).eq(0))

  const [step, setStep] = useState(
    shouldShortCircuitPaymentFlow
      ? PaymentModalSteps.PaymentCompleteZeroDue
      : PaymentModalSteps.ChooseMethodOfPayment
  )

  const [taxes, setTaxes] = useState('0.00')

  // Licensing does not contain tax, so the total is just the amountDue amount
  const amountDueBigValue = convertTwoDecimalStringToBig(amountDue.amount)
  const total = !isLicensing ? amountDueBigValue.plus(taxes) : amountDueBigValue

  const [validAddresses, setValidAddresses] = useState<Address[]>([])

  const {
    billingAddressInformation,
    setBillingAddressInformation,
    requiredBillingInformationPresent,
  } = useBillingAddressForm()

  const {
    bankPaymentInformation,
    setBankPaymentInformation,
    requiredPaymentInformationPresent,
  } = useBankPaymentForm()

  const {
    amountOption,
    setAmountOption,
    amountOfPayment,
    setAmountOfPayment,
    isAmountOfPaymentValid,
    handleAmountInputBlur,
    handleAmountOfPaymentChange,
    handleKeyDown,
  } = usePaymentAmount({ total, defaultAmountOption: initialAmountOption })

  const [updateStateOptions, setUpdateStateOptions] = useState(false)

  useEffect(() => {
    if (updateStateOptions) {
      addLoadingIds([fetchStatesLoadingId])
      setUpdateStateOptions(false)
    }
  }, [fetchStatesLoadingId, addLoadingIds, updateStateOptions])

  const [bankAccountMatch, setBankAccountMatch] = useState<FieldValidity>({
    input: true,
  })

  const [validRoutingNumber, setValidRoutingNumber] = useState<FieldValidity>({
    input: true,
  })

  /** Value must be JSON parse-able which is why it is a string */
  const initialSelectedValidAddress = 'null'

  const validationMessageMap = useValidationMessages(
    getPaymentValidationMessagesConfig(t)
  )

  const defaultMessageError = validationMessageMap.get(
    ValidationMessagesEnum.defaultMessage
  ) as string
  const bankAccountsDoesNotMatch = validationMessageMap.get(
    ValidationMessagesEnum.accountNumber
  ) as string
  const bankAccountNumberMatch = validationMessageMap.get(
    ValidationMessagesEnum.bankAccountNumber
  ) as string
  const confirmBankAccountNumberMatch = validationMessageMap.get(
    ValidationMessagesEnum.confirmBankAccountNumber
  ) as string

  const [paymentInformation, setPaymentInformation] =
    useState<PaymentResponse>()

  const [errorMessage, setErrorMessage] = useState('')
  const [errorProcessingPayment, setErrorProcessingPayment] = useState(false)
  const [allowRestart, setAllowRestart] = useState(true)

  const [submissionInProgress, setSubmissionInProgress] = useState(false)

  /** Methods */
  const resetFields = () => {
    setAllowRestart(true)
    setErrorProcessingPayment(false)
    setErrorMessage('')
    setAmountOption(initialAmountOption)
    setAmountOfPayment('')
    setTaxes('0.00')
    setIsCardSelected(false)
    setIsBankSelected(isLicensing)
    setSubmissionInProgress(false)
    setBillingAddressInformation({
      fullName: '',
      address: emptyAddressInformation,
    })
    setBankPaymentInformation({
      firstName: '',
      lastName: '',
      routingNumber: '',
      bankAccountNumber: '',
      confirmBankAccountNumber: '',
    })
    setLCDAmountOption('payTotalAmountDue')
    setLcdProgramSelections([])
  }

  const reloadPage = () => {
    setStep(PaymentModalSteps.ChooseMethodOfPayment)
    resetFields()
  }

  const onCancel = () => {
    setErrorMessage('')
    setCancelProcess(true)
  }

  const afterClose = () => {
    setStep(PaymentModalSteps.ChooseMethodOfPayment)
    resetFields()
    setCancelProcess(false)
  }

  const updateBillingPaymentAddress = async (selectedAddress?: Address) => {
    if (!!paymentInformation) {
      try {
        const updatedPaymentInformation = await updatePaymentAddress({
          paymentKey: paymentInformation.payment.paymentKey,
          body: selectedAddress ?? billingAddressInformation.address,
        })

        setTaxes(updatedPaymentInformation.payment.tax.amount)
        setPaymentInformation(updatedPaymentInformation)
        setErrorMessage('')
        setStep(PaymentModalSteps.EnterPaymentInformation)
        setStepToValidatePaymentBillingAddress(
          initialValueForPaymentBillingAddress
        )
        setSelectedValidAddress(initialSelectedValidAddress)
      } catch (e) {
        const errorObject = (await extractedErrorObject(e)) ?? {
          code: 'Unknown',
          message: (e as unknown as Error).message ?? defaultErrorMessage,
        }
        setErrorMessage(errorObject.message)
      } finally {
        setSubmissionInProgress(false)
      }
    }
  }

  const validateAddress = async (): Promise<boolean> => {
    setStepToValidatePaymentBillingAddress(
      PaymentBillingAddressValidationSteps.ValidatingAddress
    )

    const result = await validateAddressUtil(
      billingAddressInformation.address,
      true
    )

    if (!result.success) {
      setSubmissionInProgress(false)
      setStepToValidatePaymentBillingAddress(
        PaymentBillingAddressValidationSteps.UnverifiedAddress
      )

      return false
    }

    if (result.validAddresses && result.shouldSelectAddress) {
      setSubmissionInProgress(false)
      setValidAddresses(result.validAddresses)
      setStepToValidatePaymentBillingAddress(
        PaymentBillingAddressValidationSteps.SelectValidAddress
      )
      setSelectedValidAddress(JSON.stringify(result.validAddresses[0]))
      return false
    }

    return true
  }

  const geocodeAddress = async (): Promise<boolean> => {
    setStepToValidatePaymentBillingAddress(
      PaymentBillingAddressValidationSteps.ValidatingAddress
    )

    const result = await geocodeAddressUtil(billingAddressInformation.address)

    if (!result.success) {
      if (result.isDenied) {
        setStepToValidatePaymentBillingAddress(
          PaymentBillingAddressValidationSteps.DeniedAddress
        )
      } else if (result.error) {
        setErrorMessage(result.error)
        setStepToValidatePaymentBillingAddress(
          PaymentBillingAddressValidationSteps.UnverifiedAddress
        )
      }
      return false
    }

    return true
  }

  const handleFormSubmit = async (event: React.FormEvent<HTMLDivElement>) => {
    event.preventDefault()
    setSubmissionInProgress(true)

    if (cancelProcess || errorProcessingPayment) {
      yesCancel()
    } else if (step === PaymentModalSteps.ChooseMethodOfPayment) {
      await handleChoosePaymentMethod(
        {
          isLicensing,
          isBankSelected,
          isLcd,
          total,
          totalAchDiscount,
          unpaidEnrollments,
          programKey,
          amountOfPayment,
          actorKey,
          defaultErrorMessage,
        },
        setPaymentInformation,
        setStep,
        setErrorMessage,
        setSubmissionInProgress
      )
    } else if (step === PaymentModalSteps.EnterAmount) {
      await handleEnterAmount(
        {
          programKey,
          amountOfPayment,
          total,
          actorKey,
          defaultErrorMessage,
        },
        setPaymentInformation,
        setStep,
        setErrorMessage,
        setSubmissionInProgress
      )
    } else if (step === PaymentModalSteps.LCDStep) {
      await handleLCDStep(
        lcdAmountOption,
        programs ?? [],
        amountDue,
        amountOwed ?? { amount: '0.00', currencyCode: defaultCurrencyCode },
        actorKey,
        setPaymentInformation,
        setStep,
        setErrorMessage,
        setSubmissionInProgress
      )
    } else if (step === PaymentModalSteps.LCDAmountStep) {
      const totalAmount = lcdProgramSelections.reduce(
        (sum, selection) => sum.plus(selection.amount),
        Big(0)
      )

      await handleLCDAmountStep(
        lcdProgramSelections,
        totalAmount,
        actorKey,
        setPaymentInformation,
        setStep,
        setErrorMessage,
        setSubmissionInProgress
      )
    } else if (
      step === PaymentModalSteps.EnterBillingAddress &&
      (stepToValidatePaymentBillingAddress ===
        PaymentBillingAddressValidationSteps.UnverifiedAddress ||
        stepToValidatePaymentBillingAddress ===
          PaymentBillingAddressValidationSteps.SelectValidAddress ||
        stepToValidatePaymentBillingAddress ===
          initialValueForPaymentBillingAddress)
    ) {
      await handleEnterBillingAddress(
        {
          stepToValidatePaymentBillingAddress,
          validateAddress,
          geocodeAddress,
          updateBillingPaymentAddress,
          selectedValidAddress,
          billingAddressInformation,
          initialValueForPaymentBillingAddress,
        },
        setStepToValidatePaymentBillingAddress,
        setBillingAddressInformation
      )
    } else if (step === PaymentModalSteps.EnterPaymentInformation) {
      await handleEnterPaymentInformation(
        {
          bankPaymentInformation,
          bankAccountMatch,
          paymentInformation,
          isBankSelected,
          bankAccountsDoesNotMatch,
          defaultMessageError,
          defaultAchErrorMessage,
          contactCustomerSupportMessage,
          validRoutingNumber,
        },
        setBankAccountMatch,
        setValidRoutingNumber,
        setSubmissionInProgress,
        setErrorMessage,
        setTaxes,
        setPaymentInformation,
        setStep,
        setErrorProcessingPayment,
        setAllowRestart,
        addLoadingIds,
        loadingIdsToAddOnComplete
      )
    } else {
      yesCancel()
      setSubmissionInProgress(false)
    }
  }

  const backToLCDStepFromBillingAddress = () => {
    setErrorMessage('')
    setStep(PaymentModalSteps.LCDStep)

    setBillingAddressInformation({
      fullName: '',
      address: emptyAddressInformation,
    })
    setSelectedValidAddress(initialSelectedValidAddress)
    setStepToValidatePaymentBillingAddress(initialValueForPaymentBillingAddress)
  }

  const backToBillingAddress = () => {
    setBankPaymentInformation({
      firstName: '',
      lastName: '',
      routingNumber: '',
      bankAccountNumber: '',
      confirmBankAccountNumber: '',
    })
    setErrorMessage('')
    setStep(PaymentModalSteps.EnterBillingAddress)
    setSelectedValidAddress(initialSelectedValidAddress)
    setStepToValidatePaymentBillingAddress(initialValueForPaymentBillingAddress)
  }

  const backToMethodOfPayment = () => {
    if (isLicensing) {
      setAmountOfPayment('')
    } else {
      setTaxes('0.00')
      setBillingAddressInformation({
        fullName: '',
        address: emptyAddressInformation,
      })
    }
    setErrorMessage('')
    setStep(PaymentModalSteps.ChooseMethodOfPayment)
    setSelectedValidAddress(initialSelectedValidAddress)
    setStepToValidatePaymentBillingAddress(initialValueForPaymentBillingAddress)
  }

  const backToEnterAmount = () => {
    setBillingAddressInformation({
      fullName: '',
      address: emptyAddressInformation,
    })
    setErrorMessage('')
    setStep(PaymentModalSteps.EnterAmount)
  }

  const backToLCDStep = () => {
    setErrorMessage('')
    setStep(PaymentModalSteps.LCDStep)
  }

  const handleLCDProgramSelectionsChange = (
    selections: LcdProgramSelection[]
  ) => {
    setLcdProgramSelections(selections)
    const programKeys = selections.map((s) => s.programKey)
    setSelectedPrograms(programKeys)
  }

  /** Store a stringified version of the address in state to work with radio selection. */
  const [selectedValidAddress, setSelectedValidAddress] = useState(
    initialSelectedValidAddress
  )
  const handleAddressSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedValidAddress(event.target.value)
  }

  const DialogActions = () => {
    const maximumPaymentAmount = 10000
    let primaryButtonLabel = ContainedButtonVariant.Next
    let secondaryButtonLabel: TextButtonVariant | undefined =
      TextButtonVariant.Cancel
    let tertiaryButtonLabel: TextButtonVariant | undefined =
      TextButtonVariant.Cancel
    let secondaryClick = onCancel
    let tertiaryClick = onCancel
    let disablePrimaryButton = false
    if (errorProcessingPayment) {
      primaryButtonLabel = ContainedButtonVariant.Close
      secondaryButtonLabel = undefined
      tertiaryButtonLabel = undefined
      disablePrimaryButton = false
    } else {
      if (!cancelProcess) {
        switch (step) {
          case PaymentModalSteps.EnterAmount:
            primaryButtonLabel = ContainedButtonVariant.Next
            secondaryButtonLabel = TextButtonVariant.Back
            tertiaryButtonLabel = TextButtonVariant.Cancel
            disablePrimaryButton =
              amountOption !== initialAmountOption
                ? !isAmountOfPaymentValid.input ||
                  !isAmountOfPaymentValid.beforeMax ||
                  !amountOfPayment ||
                  (isValidTwoDecimalString(amountOfPayment) &&
                    convertTwoDecimalStringToBig(amountOfPayment).gt(
                      maximumPaymentAmount
                    ))
                : amountDueBigValue.gt(maximumPaymentAmount)

            secondaryClick = backToMethodOfPayment
            tertiaryClick = onCancel
            break
          case PaymentModalSteps.LCDStep:
            primaryButtonLabel = ContainedButtonVariant.Next
            secondaryButtonLabel = TextButtonVariant.Back
            tertiaryButtonLabel = TextButtonVariant.Cancel
            disablePrimaryButton = false
            secondaryClick = backToMethodOfPayment
            tertiaryClick = onCancel
            break
          case PaymentModalSteps.LCDAmountStep:
            primaryButtonLabel = ContainedButtonVariant.Next
            secondaryButtonLabel = TextButtonVariant.Back
            tertiaryButtonLabel = TextButtonVariant.Cancel
            disablePrimaryButton = lcdTotalAmount.eq(0)
            secondaryClick = backToLCDStep
            tertiaryClick = onCancel
            break
          case PaymentModalSteps.PaymentComplete:
          case PaymentModalSteps.PaymentCompleteZeroDue:
            primaryButtonLabel = ContainedButtonVariant.Close
            secondaryButtonLabel = undefined
            tertiaryButtonLabel = undefined
            disablePrimaryButton = false
            break
          case PaymentModalSteps.EnterPaymentInformation:
            primaryButtonLabel = ContainedButtonVariant.Pay
            secondaryButtonLabel = TextButtonVariant.Back
            tertiaryButtonLabel = TextButtonVariant.Cancel
            // Update for isCardSelect when implementing https://projekt202.atlassian.net/browse/CCP1-2335
            disablePrimaryButton = isCardSelected
              ? false
              : !requiredPaymentInformationPresent
            secondaryClick = backToBillingAddress
            tertiaryClick = onCancel
            break
          case PaymentModalSteps.EnterBillingAddress:
            primaryButtonLabel = ContainedButtonVariant.Next
            secondaryButtonLabel = TextButtonVariant.Back
            tertiaryButtonLabel = TextButtonVariant.Cancel
            disablePrimaryButton = !requiredBillingInformationPresent
            secondaryClick = isLcd
              ? backToLCDStepFromBillingAddress
              : isLicensing
              ? backToEnterAmount
              : backToMethodOfPayment
            tertiaryClick = onCancel
            switch (stepToValidatePaymentBillingAddress) {
              case PaymentBillingAddressValidationSteps.UnverifiedAddress:
                primaryButtonLabel = ContainedButtonVariant.UseAnyway
                secondaryButtonLabel = TextButtonVariant.Back
                tertiaryButtonLabel = TextButtonVariant.Cancel
                disablePrimaryButton = !requiredBillingInformationPresent
                secondaryClick = isLicensing
                  ? backToBillingAddress
                  : backToMethodOfPayment
                tertiaryClick = onCancel
                break
              case PaymentBillingAddressValidationSteps.SelectValidAddress:
                primaryButtonLabel = ContainedButtonVariant.Confirm
                secondaryButtonLabel = TextButtonVariant.Back
                tertiaryButtonLabel = TextButtonVariant.Cancel
                disablePrimaryButton = !requiredBillingInformationPresent
                secondaryClick = isLicensing
                  ? backToBillingAddress
                  : backToMethodOfPayment
                tertiaryClick = onCancel
                break
            }
            break
          case PaymentModalSteps.ChooseMethodOfPayment:
          default:
            primaryButtonLabel = ContainedButtonVariant.Next
            secondaryButtonLabel = TextButtonVariant.Cancel
            disablePrimaryButton = !isBankSelected && !isCardSelected
            tertiaryButtonLabel = undefined
            secondaryClick = onCancel
        }
      } else {
        primaryButtonLabel = ContainedButtonVariant.YesCancel
        secondaryButtonLabel = TextButtonVariant.NoGoBack
        tertiaryButtonLabel = undefined
        secondaryClick = () => setCancelProcess(false)
        disablePrimaryButton = false
      }
    }
    return (
      !(
        isCardSelected && step === PaymentModalSteps.EnterPaymentInformation
      ) && (
        <ActionButtons
          primaryButtonLabel={primaryButtonLabel}
          disablePrimaryButton={submissionInProgress || disablePrimaryButton}
          secondaryClick={secondaryClick}
          tertiaryClick={tertiaryClick}
          secondaryButtonLabel={secondaryButtonLabel}
          tertiaryButtonLabel={tertiaryButtonLabel}
        />
      )
    )
  }

  const DialogTitle = () => {
    if (!cancelProcess) {
      switch (step) {
        case PaymentModalSteps.EnterAmount:
          return t('PaymentModal.Heading.EnterAmount', 'Enter Amount')
        case PaymentModalSteps.LCDStep:
          return t(
            'PaymentModal.Heading.LCD',
            'Enter Amount (Does not include tax)'
          )
        case PaymentModalSteps.LCDAmountStep:
          return t(
            'PaymentModal.Heading.LCDAmount',
            'Enter Amount (Does not include tax)'
          )
        case PaymentModalSteps.PaymentComplete:
          return isCardSelected
            ? t(
                'PaymentModal.Heading.ThankYouForPayment',
                'Thank you for your payment'
              )
            : t(
                'PaymentModal.Heading.PaymentSubmitted',
                'Your payment has been submitted'
              )
        case PaymentModalSteps.PaymentCompleteZeroDue:
          return t(
            'PaymentModal.Heading.StudentsEnrolled',
            'Students successfully enrolled'
          )
        case PaymentModalSteps.EnterPaymentInformation:
          return t(
            'PaymentModal.Heading.EnterPaymentInformation',
            'Enter Payment Information'
          )
        case PaymentModalSteps.EnterBillingAddress:
          return t(
            'PaymentModal.Heading.EnterBillingAddress',
            'Enter Billing Address'
          )
        case PaymentModalSteps.ChooseMethodOfPayment:
        default:
          return t(
            'PaymentModal.Heading.ChooseMethodOfPayment',
            'Choose a Method of Payment'
          )
      }
    } else {
      return t(
        'PaymentModal.Heading.Cancel',
        'Are you sure you want to cancel this payment process?'
      )
    }
  }

  const selectCreditOrDebit = () => {
    setIsCardSelected(true)
    setIsBankSelected(false)
  }

  const selectBankAccount = () => {
    setIsCardSelected(false)
    setIsBankSelected(true)
  }

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (step === PaymentModalSteps.EnterBillingAddress) {
      /** We trim the input value to account for user mistake. */
      setBillingAddressInformation({
        ...billingAddressInformation,
        [event.target.name]: event.target.value.trim(),
      })
    }
    // Only other step with fields is Payment Information
    else {
      // Then we check selection
      if (isBankSelected) {
        if (event.target.name === 'accountType') {
          setBankPaymentInformation({
            ...bankPaymentInformation,
          })
        }
      }
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (step === PaymentModalSteps.EnterBillingAddress) {
      if (event.target.name === 'fullName') {
        setBillingAddressInformation({
          ...billingAddressInformation,
          [event.target.name]: event.target.value,
        })
        return
      }
      /** Clear state field when country changes to avoid MUI warning. */
      if (event.target.name === 'countryCode') {
        setBillingAddressInformation({
          ...billingAddressInformation,
          address: {
            ...billingAddressInformation.address,
            [event.target.name]: event.target.value,
            state: '',
          },
        })
        if (!!event.target.value) {
          setUpdateStateOptions(true)
        }
      }
      setBillingAddressInformation({
        ...billingAddressInformation,
        address: {
          ...billingAddressInformation.address,
          [event.target.name]: event.target.value,
        },
      })
    }
    // Only other step with fields is Payment Information
    else {
      // Then we check selection
      if (isBankSelected) {
        if (
          event.target.name === 'routingNumber' ||
          event.target.name === 'bankAccountNumber' ||
          event.target.name === 'confirmBankAccountNumber'
        ) {
          const value = event.target.value.replace(/[^0-9]/g, '')
          setBankPaymentInformation({
            ...bankPaymentInformation,
            [event.target.name]: value,
          })
        } else {
          setBankPaymentInformation({
            ...bankPaymentInformation,
            [event.target.name]: event.target.value,
          })
        }
      }
    }
  }

  /** Other Objects */
  const defaultErrorMessage = t(
    'PaymentModal.ErrorMessage.Default',
    'This payment system is temporarily unavailable. Please cancel the payment process and try again later.'
  )

  const defaultAchErrorMessage = t(
    'PaymentModal.Error.PayByAch',
    'The ACH payment provider encountered an error processing the payment. Please verify your billing address and payment information and try again. If the problem persists please contact Customer Support.'
  )

  const contactCustomerSupportMessage = t(
    'PaymentModal.EnterPaymentInformation.Error.Blocking',
    'The ACH payment provider encountered an error processing your payment. Please contact Customer Support.'
  )

  const sharedFieldProps = {
    onBlur: handleInputBlur,
    onChange: handleInputChange,
  }

  const totalAchDiscount = convertTwoDecimalStringToBig(
    achDiscount.amount
  ).times(achDiscount.applyDiscountTimes)

  /** Hooks */
  useEffect(() => {
    const beginPaymentForZeroAmountDue = async () => {
      try {
        const resultingPayment = await startPayment({
          body: {
            _for: {
              type: PaymentStartRequestBodyForTypeEnum.Enrollment,
              enrollments: unpaidEnrollments ?? [],
            },
            amount: amountDue.amount,
            method: PaymentMethodKey.NA,
          },
        })
        setPaymentInformation(resultingPayment)
      } catch (e) {
        const errorObject = (await extractedErrorObject(e)) ?? {
          code: 'Unknown',
          message: (e as unknown as Error).message ?? defaultErrorMessage,
        }
        setStep(PaymentModalSteps.ChooseMethodOfPayment)
        setErrorMessage(errorObject.message)
      }
    }

    if (shouldShortCircuitPaymentFlow && isOpen) {
      beginPaymentForZeroAmountDue()
    }
  }, [
    amountDue.amount,
    defaultErrorMessage,
    isOpen,
    shouldShortCircuitPaymentFlow,
    unpaidEnrollments,
  ])

  const autoFillCountryCode = useCallback(() => {
    if (!billingAddressInformation.address.countryCode && isBankSelected) {
      setBillingAddressInformation({
        ...billingAddressInformation,
        address: {
          ...billingAddressInformation.address,
          countryCode: 'US',
        },
      })
    }
  }, [billingAddressInformation, isBankSelected, setBillingAddressInformation])

  useEffect(() => {
    if (step === PaymentModalSteps.EnterBillingAddress) {
      autoFillCountryCode()
    }
  }, [step, autoFillCountryCode])

  const DialogContent = () => {
    if (errorProcessingPayment) {
      return (
        <ErrorAlert
          error={errorMessage}
          button={
            allowRestart ? (
              <Button
                sx={{ color: theme.palette.error.main }}
                onClick={reloadPage}
              >
                {t('TeamInvite.Button.Restart', 'Restart')}
              </Button>
            ) : undefined
          }
        />
      )
    }

    if (!cancelProcess) {
      switch (step) {
        case PaymentModalSteps.EnterAmount:
          return (
            <EnterAmountContent
              amountOption={amountOption}
              setAmountOption={setAmountOption}
              amountOfPayment={amountOfPayment}
              isAmountOfPaymentValid={isAmountOfPaymentValid}
              handleAmountOfPaymentChange={handleAmountOfPaymentChange}
              handleKeyDown={handleKeyDown}
              handleAmountInputBlur={handleAmountInputBlur}
              amountDue={amountDue}
              defaultCurrencyCode={defaultCurrencyCode}
            />
          )
        case PaymentModalSteps.LCDStep:
          return (
            <LCDOptionsContent
              amountOption={lcdAmountOption}
              setAmountOption={setLCDAmountOption}
              totalAmountDue={{
                amount: amountDue.amount,
                currencyCode: amountDue.currencyCode,
              }}
              totalAmountOwed={{
                amount: amountOwed?.amount ?? '0.00',
                currencyCode: amountOwed?.currencyCode ?? defaultCurrencyCode,
              }}
              defaultCurrencyCode={defaultCurrencyCode}
            />
          )
        case PaymentModalSteps.LCDAmountStep:
          return (
            <LCDProgramSelectionComponent
              programs={programs ?? []}
              selectedPrograms={selectedPrograms}
              setSelectedPrograms={setSelectedPrograms}
              onChange={handleLCDProgramSelectionsChange}
            />
          )
        case PaymentModalSteps.PaymentComplete: {
          const getTotalForLCD = () => {
            // Use the actual payment amount from paymentInformation if available
            // This prevents the amount from resetting to 0 when props change
            if (paymentInformation?.payment?.amount?.amount) {
              return convertTwoDecimalStringToBig(
                paymentInformation.payment.amount.amount
              )
            }
            if (!isLcd) {
              return total
            }
            switch (lcdAmountOption) {
              case 'enterDifferentAmount':
                return lcdTotalAmount
              case 'payTotalAmountOwed':
                return convertTwoDecimalStringToBig(
                  amountOwed?.amount ?? '0.00'
                )
              case 'payTotalAmountDue':
              default:
                return total
            }
          }
          return (
            <PaymentCompleteContent
              isCardSelected={isCardSelected}
              isBankSelected={isBankSelected}
              isLicensing={isLicensing}
              total={getTotalForLCD()}
              amountOfPayment={amountOfPayment}
              isLcd={isLcd}
            />
          )
        }
        case PaymentModalSteps.PaymentCompleteZeroDue:
          return <PaymentCompleteZeroDueContent />
        case PaymentModalSteps.EnterPaymentInformation:
          return (
            <EnterPaymentInformationContent
              isLicensing={isLicensing}
              isBankSelected={isBankSelected}
              isCardSelected={isCardSelected}
              amountDue={amountDue}
              paymentInformation={paymentInformation}
              taxes={taxes}
              totalAchDiscount={totalAchDiscount}
              amountDueBigValue={amountDueBigValue}
              total={total}
              sharedFieldProps={sharedFieldProps}
              bankPaymentInformation={bankPaymentInformation}
              validRoutingNumber={validRoutingNumber}
              bankAccountMatch={bankAccountMatch}
              bankAccountNumberMatch={bankAccountNumberMatch}
              confirmBankAccountNumberMatch={confirmBankAccountNumberMatch}
              setErrorMessage={setErrorMessage}
              backToBillingAddress={backToBillingAddress}
              setStep={setStep}
            />
          )
        case PaymentModalSteps.EnterBillingAddress:
          switch (stepToValidatePaymentBillingAddress) {
            case PaymentBillingAddressValidationSteps.ValidatingAddress:
              return (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={200}
                >
                  <CircularProgress />
                </Box>
              )
            case PaymentBillingAddressValidationSteps.UnverifiedAddress:
              return (
                <InvalidAddress
                  enteredAddress={billingAddressInformation.address}
                  variant={InvalidAddressVariant.Unverified}
                  onEditAddressClick={async () => {
                    setErrorMessage('')
                    backToBillingAddress()
                  }}
                  withEditAddressButton={!isLicensing}
                />
              )
            case PaymentBillingAddressValidationSteps.SelectValidAddress:
              return (
                <AddressSelect
                  enteredAddress={billingAddressInformation.address}
                  validAddresses={validAddresses}
                  selection={selectedValidAddress}
                  onSelectionChange={handleAddressSelectionChange}
                  onEditAddressClick={() => {
                    setErrorMessage('')
                    backToBillingAddress()
                  }}
                  withEditAddressButton={!isLicensing}
                />
              )
            case PaymentBillingAddressValidationSteps.DeniedAddress:
              return (
                <InvalidAddress
                  enteredAddress={billingAddressInformation.address}
                  variant={InvalidAddressVariant.Denied}
                />
              )
            default:
              return (
                <>
                  <Box mb={3}>
                    <TextField
                      {...sharedFieldProps}
                      id="fullName"
                      name="fullName"
                      variant="filled"
                      label={t('PaymentModal.Field.FullName', 'Full Name')}
                      value={billingAddressInformation.fullName}
                      fullWidth
                    />
                  </Box>
                  <AddressForm
                    addressInformation={billingAddressInformation.address}
                    onInputBlur={handleInputBlur}
                    onInputChange={handleInputChange}
                    countryOptions={countryOptions}
                    billingAddress
                    isCardSelected={isCardSelected}
                  />
                </>
              )
          }
        case PaymentModalSteps.ChooseMethodOfPayment:
        default:
          return (
            <PaymentMethodSelection
              isLicensing={isLicensing}
              isCardSelected={isCardSelected}
              isBankSelected={isBankSelected}
              achAllowed={achAllowed}
              achDisplayMessage={
                achDiscount.displayMessage
                  ? `${getLocaleCurrencyForAmount(achDiscount.amount, 'USD')}`
                  : undefined
              }
              achDiscountAmount={getLocaleCurrencyForAmount(
                achDiscount.amount,
                'USD'
              )}
              onSelectCreditOrDebit={selectCreditOrDebit}
              onSelectBankAccount={selectBankAccount}
            />
          )
      }
    }
  }

  return (
    <BasicModal
      isOpen={isOpen}
      afterClose={afterClose}
      handleFormSubmit={handleFormSubmit}
      maxWidth="sm"
      dialogTitle={DialogTitle()}
      dialogContent={
        <>
          {!errorProcessingPayment && !!errorMessage && (
            <ErrorAlert error={errorMessage} />
          )}
          {DialogContent()}
        </>
      }
      dialogActions={DialogActions()}
      // This is a special case where we don't want the modal to default as a form
      disableForm={
        step === PaymentModalSteps.EnterPaymentInformation &&
        !cancelProcess &&
        !isBankSelected
      }
      headerIcon={
        step === PaymentModalSteps.PaymentComplete ||
        step === PaymentModalSteps.PaymentCompleteZeroDue ? (
          <CheckCircle
            sx={{
              color: theme.palette.customBackground.stepperIcon,
              width: '50px',
              height: '50px',
            }}
          />
        ) : undefined
      }
      useBrand
    />
  )
}

export default PaymentModal
